/**
 * NFP Cycle Logic Engine
 * Implements Marquette Method rules for fertility awareness
 */

import { differenceInCalendarDays, format, parseISO } from "date-fns";
import { getDateKeyInTimeZone } from "./dateTime";
import { getTodayKey, isPastCycle } from "./cycleTiming";

export type MonitorStatus = "low" | "high" | "peak";
export type PostPeakDay = "2 PEAK" | "1 HIGH" | "2 LOW" | "complete";
export type FertilityAdvice = "avoid" | "ok" | "fertile";
export type NFPMethod = "Marquette" | "Creighton" | "Sympto-Thermal" | "Billings" | "Other";
export type Goal = "TTC" | "AP" | "Single" | "Pregnant";

/** Single source of truth for method selectable in Settings/Onboarding UI. */
export const NFP_METHOD_OPTIONS: { value: NFPMethod; label: string }[] = [
  { value: "Sympto-Thermal", label: "Sympto-Thermal" },
  { value: "Marquette", label: "Marquette" },
  { value: "Creighton", label: "Creighton" },
  { value: "Billings", label: "Billings" },
  { value: "Other", label: "Other" },
];

/** Creighton/Billings teach the detailed consistency+color mucus vocabulary. */
export const usesExpandedMucus = (methods: readonly string[]): boolean =>
  methods.some((m) => m === "Creighton" || m === "Billings");

/** Methods whose primary/only fertility signal is the monitor device. */
export const usesMonitor = (methods: readonly string[]): boolean =>
  methods.some((m) => m === "Marquette" || m === "Billings" || m === "Other");

export const usesSymptoThermal = (methods: readonly string[]): boolean =>
  methods.includes("Sympto-Thermal");

/**
 * True once a cycle day is more than 3 days past the identified Peak Day — mucus observed
 * on or after this point isn't interpreted as a fertile signal, even if it looks peak-type,
 * since the post-peak taper window (Peak Day + 3) has already closed.
 */
/**
 * Monitor readings are stored uppercase ('LOW' | 'HIGH' | 'PEAK' — see the observations
 * table CHECK constraint) while this module's rules are written against the lowercase
 * MonitorStatus values. Every comparison goes through here so a raw-string mismatch can't
 * silently switch monitor-driven peak detection off.
 */
export const normalizeMonitor = (
  value: string | null | undefined
): MonitorStatus | null => {
  const normalized = (value || "").toLowerCase();
  return normalized === "peak" || normalized === "high" || normalized === "low"
    ? normalized
    : null;
};

/**
 * @deprecated Peak + 3 only — wrong for Sympto-Thermal (double check), a restarted mucus count,
 * or an unmarked next cycle. Use isPostPeakPhaseDay (phase spans) or isAfterFertileWindow.
 */
export const isPostPeakNonFertileDay = (
  cycleDay: number,
  firstPeakCD: number | null | undefined
): boolean => firstPeakCD != null && cycleDay > firstPeakCD + 3;

interface Observation {
  date: string;
  cycle_day: number;
  bleeding?: number | null;
  monitor?: string | null;
  intercourse?: boolean | null;
  mucus?: string | null;
  mucus_consistency?: string | null;
  mucus_color?: string | null;
  temperature_f?: number | null;
  ovulation_test?: string | null;
}

/**
 * Creighton/Billings-style mucus consistency options. Shown only for those methods, which
 * teach a detailed, standardized sensation vocabulary (Marquette and Sympto-Thermal don't).
 * "Lubricative" is intentionally not a consistency variant here — it's a color/appearance
 * quality (see MUCUS_COLOR_OPTIONS) and combining it with a consistency would double-encode
 * the same observation (e.g. "Wet, Lubricative" instead of a redundant "Wet (Lubricative)").
 */
export const MUCUS_CONSISTENCY_OPTIONS = [
  { value: "dry", label: "Dry / None", peak: false },
  { value: "sticky", label: "Sticky / Tacky", peak: false },
  { value: "damp", label: "Damp", peak: false },
  { value: "stretchy", label: "Stretchy", peak: true },
  { value: "wet", label: "Wet", peak: true },
  { value: "shiny", label: "Shiny", peak: true },
] as const;

/** Creighton/Billings-style mucus color/appearance options, tracked separately from consistency. */
export const MUCUS_COLOR_OPTIONS = [
  { value: "clear", label: "Clear" },
  { value: "cloudy-clear", label: "Cloudy/Clear" },
  { value: "cloudy", label: "Cloudy" },
  { value: "gummy", label: "Gummy" },
  { value: "pasty", label: "Pasty (Creamy)" },
  { value: "lubricative", label: "Lubricative" },
  { value: "yellow", label: "Yellow" },
  { value: "brown", label: "Brown" },
  { value: "red", label: "Red" },
] as const;

/** Standard Creighton finger-test frequency notation for the day's most significant mucus sign. */
export const MUCUS_FREQUENCY_OPTIONS = [
  { value: "1x", label: "1x" },
  { value: "2x", label: "2x" },
  { value: "3x", label: "3x" },
  { value: "AD", label: "All Day (AD)" },
] as const;

export type OvulationTestResult = "positive" | "negative";

/**
 * OPK / LH test strip result. NULL ("not tested") is the third state and is never written as a
 * value. Display and corroboration only — no method defines Peak Day by an ovulation test, so
 * this never feeds the Peak or fertile-window rules.
 */
export const OVULATION_TEST_OPTIONS = [
  { value: "positive", label: "Positive" },
  { value: "negative", label: "Negative" },
] as const;

export const normalizeOvulationTest = (
  value: string | null | undefined
): OvulationTestResult | null =>
  value === "positive" || value === "negative" ? value : null;

export const isPositiveOvulationTest = (value: string | null | undefined): boolean =>
  normalizeOvulationTest(value) === "positive";

/** First cycle day with a positive OPK — the LH surge. Null when none logged. */
export const findLhSurgeDay = (
  observations: { cycle_day: number; ovulation_test?: string | null }[]
): number | null => {
  let surge: number | null = null;
  for (const obs of observations) {
    if (!isPositiveOvulationTest(obs.ovulation_test)) continue;
    if (surge === null || obs.cycle_day < surge) surge = obs.cycle_day;
  }
  return surge;
};

export const describeLhSurge = (lhSurgeCd: number | null): string | null =>
  lhSurgeCd === null
    ? null
    : `Positive ovulation test on CD ${lhSurgeCd} (LH surge; ovulation usually follows within about a day)`;

/**
 * Simpler mucus vocabulary for Marquette/Sympto-Thermal/Other, where mucus is a secondary,
 * optional signal rather than the primary indicator. Values match what the app has always
 * written for these methods, so no legacy-mapping is needed for their historical entries.
 */
export const MUCUS_CONSISTENCY_OPTIONS_BASIC = [
  { value: "dry", label: "Dry / None", peak: false },
  { value: "sticky", label: "Sticky / Tacky", peak: false },
  { value: "stretchy", label: "Stretchy", peak: true },
  { value: "clear-stretchy", label: "Clear & Stretchy", peak: true },
  { value: "wet-slippery", label: "Wet & Slippery", peak: true },
] as const;

/** Bleeding intensity/type codes. Values are stable DB codes, not an ordinal ranking. */
export const BLEEDING_OPTIONS = [
  { value: 4, label: "Very Light" },
  { value: 1, label: "Light" },
  { value: 2, label: "Medium" },
  { value: 3, label: "Heavy" },
  { value: 5, label: "Brown" },
] as const;

/**
 * Relative intensity for charting only. Brown spotting has no true intensity of its own
 * (it's a color distinction, not a volume one) so it's ranked alongside Light for display.
 */
export const BLEEDING_INTENSITY_RANK: Record<number, number> = {
  4: 0.5,
  1: 1,
  5: 1,
  2: 2,
  3: 3,
};

const PEAK_MUCUS_CONSISTENCIES = new Set<string>(
  MUCUS_CONSISTENCY_OPTIONS.filter((o) => o.peak).map((o) => o.value)
);

// Pre-migration free-text `mucus` values that don't map 1:1 onto the new consistency
// vocabulary. Used only to display old Creighton/Billings entries correctly; never written
// going forward.
const LEGACY_MUCUS_CONSISTENCY_MAP: Record<string, { consistency: string; color?: string }> = {
  "clear-stretchy": { consistency: "stretchy", color: "clear" },
  "wet-slippery": { consistency: "wet", color: "lubricative" },
};

/**
 * Marquette/Sympto-Thermal never got the consistency/color split — their picker still uses
 * the original 5 values directly, so their historical entries need no remapping at all.
 */
export const resolveLegacyMucus = (
  legacyMucus: string | null | undefined,
  methods: readonly string[] | string | null | undefined
): { consistency: string; color: string } => {
  if (!legacyMucus) return { consistency: "", color: "" };
  const methodList = Array.isArray(methods) ? methods : methods ? [methods] : [];
  if (!usesExpandedMucus(methodList)) {
    return { consistency: legacyMucus, color: "" };
  }
  const mapped = LEGACY_MUCUS_CONSISTENCY_MAP[legacyMucus];
  if (mapped) return { consistency: mapped.consistency, color: mapped.color ?? "" };
  // "dry", "sticky", "stretchy" are unchanged values in the new vocabulary.
  return { consistency: legacyMucus, color: "" };
};

/**
 * Kept in sync with the legacy free-text `mucus` column so pages that haven't been updated
 * to read mucus_consistency/mucus_color (exports, calendar day panel) still show something.
 * Marquette/Sympto-Thermal write values (e.g. "clear-stretchy") not in the Creighton/Billings
 * list below; falls back to the raw value itself so their legacy `mucus` column keeps working.
 */
export const buildLegacyMucusSummary = (consistency: string, color: string): string | null => {
  const consistencyLabel =
    MUCUS_CONSISTENCY_OPTIONS.find((o) => o.value === consistency)?.label || consistency || undefined;
  const colorLabel = MUCUS_COLOR_OPTIONS.find((o) => o.value === color)?.label;
  const parts = [consistencyLabel, colorLabel].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : null;
};

interface Cycle {
  id: string;
  start_date: string;
  end_date?: string | null;
  predicted_avoid_start_cd?: number | null;
  first_peak_cd?: number | null;
  post_peak_progress?: string | null;
  /** Partial cycle whose real start was never recorded (see derivedCycles.ts). */
  start_inferred?: boolean | null;
}

interface CycleAnalysis {
  currentDay: number;
  fertilityAdvice: FertilityAdvice;
  reason: string;
  postPeakProgress?: PostPeakDay;
  isPostPeak: boolean;
  /** First positive ovulation test (OPK) day this cycle. Informational; never drives advice. */
  lhSurgeCd?: number | null;
}

/**
 * Calculate predicted avoid start based on historical cycles
 * Default: CD 6 for first cycles, then shortest cycle - 20
 */
export function calculatePredictedAvoidStart(
  historicalCycles: Cycle[],
  todayKey: string = getTodayKey()
): number {
  if (historicalCycles.length === 0) return 6;

  // Find shortest completed cycle. A partial cycle (start never recorded) has no real
  // length, so it can't inform the calculation.
  // Only cycles that ended before today count: a current cycle with a pre-logged next start,
  // or an upcoming one, hasn't actually been observed to that length.
  const completedCycles = historicalCycles.filter(
    (c) => !c.start_inferred && isPastCycle(c, todayKey)
  );
  if (completedCycles.length === 0) return 6;

  const shortestCycle = completedCycles.reduce((shortest, cycle) => {
    const days = daysBetween(cycle.start_date, cycle.end_date!);
    return Math.min(shortest, days);
  }, 999);

  return Math.max(6, shortestCycle - 20);
}

/**
 * Determine if mucus observation indicates peak fertility (Creighton/Billings).
 * Checks the structured consistency field first, falling back to the free-text `mucus`
 * value written by simpler methods (Marquette/Sympto-Thermal) and pre-migration entries.
 * Lubrication is checked separately since it's recorded as a color/appearance quality,
 * not a consistency value, but is itself a Peak-type signal regardless of sensation.
 */
export function isPeakMucus(
  mucusConsistency: string | null | undefined,
  legacyMucus?: string | null,
  mucusColor?: string | null
): boolean {
  if ((mucusColor || "").toLowerCase() === "lubricative") return true;
  const value = (mucusConsistency || legacyMucus || "").toLowerCase();
  if (!value) return false;
  if (PEAK_MUCUS_CONSISTENCIES.has(value)) return true;
  // Values written by simpler methods / pre-migration entries that don't map 1:1 onto the
  // Creighton/Billings consistency vocabulary above.
  const legacyPeakTypes = ["clear-stretchy", "wet-slippery"];
  return legacyPeakTypes.some((type) => value.includes(type));
}

/** Only the fields the temperature rules read, so callers needn't build a full Observation. */
export type TemperatureObservation = {
  cycle_day: number;
  temperature_f?: number | null;
};

/** Days after Peak Day that stay fertile; the day after is the first the mucus count allows. */
export const POST_PEAK_COUNT_DAYS = 3;

/** Hundredths of a degree, so threshold comparisons are exact (97.2 + 0.4 is not 97.6 in floats). */
const toHundredths = (value: number) => Math.round(value * 100);

/** Required rise above the six-day baseline average, in hundredths of a °F (0.4°F). */
const THERMAL_SHIFT_RISE_HUNDREDTHS = 40;

/** Temperatures the baseline averages over, on the days immediately before the rise. */
const THERMAL_BASELINE_DAYS = 6;

export interface ThermalShift {
  /** Cycle day of the first of the three higher temperatures. */
  shiftDay: number;
  /** Cycle day of the third higher temperature — the day the shift is confirmed. */
  thirdHighDay: number;
  /** Average (°F) of the six baseline temperatures the rise was measured against. */
  baseline: number;
}

/**
 * Every thermal shift in the cycle, in order. A shift is three temperatures on CONSECUTIVE
 * cycle days, each ≥ 0.4°F above the average of the six temperatures on the six days
 * immediately before it. Missing days are never bridged: a gap in the baseline or among the
 * three high readings means no shift is confirmed there, which keeps the window open rather
 * than guessing. A sustained plateau counts once (later days of the same rise aren't new shifts).
 * Comparisons are done in hundredths of a degree so an exact 0.4°F rise counts.
 */
export function findThermalShifts(observations: TemperatureObservation[]): ThermalShift[] {
  const tempByDay = new Map<number, number>();
  for (const obs of observations) {
    if (obs.temperature_f) tempByDay.set(obs.cycle_day, toHundredths(obs.temperature_f));
  }
  const days = [...tempByDay.keys()].sort((a, b) => a - b);

  // t ≥ sum/6 + rise  ⇔  6t ≥ sum + 6·rise — integer arithmetic, no rounding of the average.
  const isAbove = (day: number, baselineSum: number) => {
    const t = tempByDay.get(day);
    return (
      t !== undefined &&
      THERMAL_BASELINE_DAYS * t >= baselineSum + THERMAL_BASELINE_DAYS * THERMAL_SHIFT_RISE_HUNDREDTHS
    );
  };

  const shifts: (ThermalShift & { baselineSum: number })[] = [];
  for (const day of days) {
    const baselineDays = Array.from({ length: THERMAL_BASELINE_DAYS }, (_, k) => day - 1 - k);
    if (!baselineDays.every((d) => tempByDay.has(d))) continue;
    const baselineSum = baselineDays.reduce((sum, d) => sum + (tempByDay.get(d) as number), 0);
    if (!isAbove(day, baselineSum) || !isAbove(day + 1, baselineSum) || !isAbove(day + 2, baselineSum)) {
      continue;
    }

    // Still on the plateau of the previous shift (every day since it began stayed above that
    // shift's threshold): the same rise, not a new one.
    const previous = shifts[shifts.length - 1];
    if (previous) {
      let continuesPlateau = true;
      for (let d = previous.shiftDay; d < day; d += 1) {
        if (!isAbove(d, previous.baselineSum)) {
          continuesPlateau = false;
          break;
        }
      }
      if (continuesPlateau) continue;
    }

    shifts.push({
      shiftDay: day,
      thirdHighDay: day + 2,
      baseline: baselineSum / THERMAL_BASELINE_DAYS / 100,
      baselineSum,
    });
  }

  return shifts.map(({ shiftDay, thirdHighDay, baseline }) => ({ shiftDay, thirdHighDay, baseline }));
}

/**
 * Detect thermal shift (see findThermalShifts): the cycle day of the first of three
 * consecutive-day temperatures ≥ 0.4°F above the preceding six-day average, or null.
 * `fromCycleDay` skips shifts that begin earlier than that day.
 */
export function detectThermalShift(
  observations: TemperatureObservation[],
  fromCycleDay: number = 1
): number | null {
  return findThermalShifts(observations).find((s) => s.shiftDay >= fromCycleDay)?.shiftDay ?? null;
}

/** Only the fields the Peak / double-check rules read. */
export type SymptoThermalObservation = TemperatureObservation & {
  monitor?: string | null;
  mucus?: string | null;
  mucus_consistency?: string | null;
  mucus_color?: string | null;
  bleeding?: number | null;
};

const hasMucusValue = (o: SymptoThermalObservation) =>
  Boolean(o.mucus_consistency || o.mucus || o.mucus_color);

const isPeakTypeDay = (o: SymptoThermalObservation) =>
  isPeakMucus(o.mucus_consistency, o.mucus, o.mucus_color);

const sortByCycleDay = <T extends { cycle_day: number }>(observations: readonly T[]): T[] =>
  [...observations].sort((a, b) => a.cycle_day - b.cycle_day);

/**
 * How far before the first higher temperature a mucus Peak may sit and still be paired with
 * that shift. Sensiplan observes the Peak within a few days of the temperature rise; a lone
 * peak-type day further back (e.g. during menses) isn't treated as this cycle's Peak.
 */
export const ST_PEAK_MAX_DAYS_BEFORE_SHIFT = 3;

/** First monitor PEAK reading in the cycle (Marquette's Peak Day), or null. */
export function findFirstMonitorPeakDay(
  observations: SymptoThermalObservation[]
): number | null {
  const peak = sortByCycleDay(observations).find((o) => normalizeMonitor(o.monitor) === "peak");
  return peak ? peak.cycle_day : null;
}

/** Why a Peak's window hasn't closed (null when it has). */
export type PeakClosePending =
  /** Sympto-Thermal: no temperature shift confirms this Peak yet. */
  | "thermal-shift"
  /** The Peak is a peak-type mucus day and no later mucus has been recorded to confirm the change. */
  | "mucus-change"
  /** Sympto-Thermal: peak-type mucus reappeared after the window closed and no later shift confirms it. */
  | "reopened";

export interface PeakClose {
  /** The Peak after any peak-type mucus that returned before the window closed has moved it. */
  effectivePeakCd: number;
  /** Last fertile cycle day under the selected methods, or null while it can't close yet. */
  lastFertileCd: number | null;
  basis: "peak-plus-3" | "thermal-double-check" | "awaiting-thermal-shift" | "unresolved";
  pending: PeakClosePending | null;
  /** The thermal shift confirming the Peak (Sympto-Thermal only). */
  shift: ThermalShift | null;
}

/**
 * When the fertile window that a given Peak opens closes, per method — the single rule behind
 * determinePostPeakProgress (advice) and computeFertileWindow (display).
 *
 * - Monitor-only methods (Marquette/Other): Peak + 3, unchanged.
 * - Mucus rules (Creighton/Billings, and Sympto-Thermal's mucus sign): the Peak is the LAST day
 *   of peak-type mucus. Peak-type mucus returning before the window closes restarts the count
 *   from the new day, as does the next recorded mucus day being peak-type however far off (the
 *   days between weren't observed). A Peak that is itself a peak-type day needs a later
 *   non-peak mucus observation to confirm it; until then the window stays open.
 * - Sympto-Thermal: the double check — the later of Peak + 3 and the third higher temperature,
 *   and both must be seen. The confirming shift is the first one whose third high reading is
 *   no more than ST_PEAK_MAX_DAYS_BEFORE_SHIFT days before the Peak, so an earlier unrelated
 *   rise (a fever) can't confirm it. Peak-type mucus after the close, with no later shift to
 *   pair it with, reopens the window: it stays open until a new shift confirms.
 *
 * Whenever in doubt the window stays open; nothing here closes it earlier than the method does.
 */
export function getPeakClose(
  observations: SymptoThermalObservation[],
  methods: readonly string[],
  peakCd: number
): PeakClose {
  const sorted = sortByCycleDay(observations);
  const st = usesSymptoThermal(methods);
  if (!st && !usesExpandedMucus(methods)) {
    return {
      effectivePeakCd: peakCd,
      lastFertileCd: peakCd + POST_PEAK_COUNT_DAYS,
      basis: "peak-plus-3",
      pending: null,
      shift: null,
    };
  }

  const shifts = st ? findThermalShifts(sorted) : [];
  const shiftFor = (peak: number) =>
    shifts.find((s) => s.thirdHighDay >= peak - ST_PEAK_MAX_DAYS_BEFORE_SHIFT) ?? null;

  let peak = peakCd;
  // Each step moves the Peak strictly later, so this terminates within the cycle's length.
  for (;;) {
    const shift = shiftFor(peak);
    const tentativeClose = Math.max(peak + POST_PEAK_COUNT_DAYS, shift?.thirdHighDay ?? 0);
    const returning = sorted
      .filter((o) => o.cycle_day > peak && o.cycle_day <= tentativeClose && isPeakTypeDay(o))
      .at(-1);
    const nextMucus = sorted.find((o) => o.cycle_day > peak && hasMucusValue(o));
    const next = returning ?? (nextMucus && isPeakTypeDay(nextMucus) ? nextMucus : undefined);
    if (!next) break;
    peak = next.cycle_day;
  }

  const result = (
    lastFertileCd: number | null,
    basis: PeakClose["basis"],
    pending: PeakClosePending | null,
    shift: ThermalShift | null
  ): PeakClose => ({ effectivePeakCd: peak, lastFertileCd, basis, pending, shift });

  const peakDay = sorted.find((o) => o.cycle_day === peak);
  const changeSeen = sorted.some((o) => o.cycle_day > peak && hasMucusValue(o));
  if (peakDay && isPeakTypeDay(peakDay) && !changeSeen) {
    return result(null, st ? "awaiting-thermal-shift" : "unresolved", "mucus-change", null);
  }

  const mucusClose = peak + POST_PEAK_COUNT_DAYS;
  if (!st) return result(mucusClose, "peak-plus-3", null, null);

  const shift = shiftFor(peak);
  if (!shift) return result(null, "awaiting-thermal-shift", "thermal-shift", null);

  const close = Math.max(mucusClose, shift.thirdHighDay);
  if (sorted.some((o) => o.cycle_day > close && isPeakTypeDay(o))) {
    return result(null, "awaiting-thermal-shift", "reopened", shift);
  }
  return mucusClose > shift.thirdHighDay
    ? result(mucusClose, "peak-plus-3", null, shift)
    : result(shift.thirdHighDay, "thermal-double-check", null, shift);
}

/** Last fertile day for a Peak under the selected methods, or null while still open. */
export const getLastFertileCd = (
  observations: SymptoThermalObservation[],
  methods: readonly string[],
  peakCd: number
): number | null => getPeakClose(observations, methods, peakCd).lastFertileCd;

/**
 * Peak Day for methods whose Peak is only knowable in hindsight — Sympto-Thermal and the
 * mucus methods (Creighton/Billings) — alone or combined with a monitor. Computed over the
 * whole cycle, so it doesn't depend on the order observations were logged in and can land on
 * an earlier day once later observations confirm it. Returns null for monitor-only methods
 * (they use the first monitor PEAK, see computeFirstPeakDay).
 *
 * - Sympto-Thermal: each thermal shift is paired with the last peak-type mucus day from
 *   ST_PEAK_MAX_DAYS_BEFORE_SHIFT days before it through its third high reading (preferring one
 *   on or before the shift day); the LATEST pair whose window closes wins, so an early fever
 *   can't claim the Peak ahead of the real fertile mucus. With a monitor method also selected,
 *   the first monitor PEAK is the Peak when its window closes (or while it only awaits the
 *   temperature shift); the mucus pairing is the fallback.
 * - Creighton/Billings: the last day of the first peak-type run, once a non-peak day confirms
 *   it (see getPeakClose). With a monitor too, the LATER of the mucus and monitor Peaks.
 */
export function identifyPeakDay(
  observations: SymptoThermalObservation[],
  methods: readonly string[]
): number | null {
  const sorted = sortByCycleDay(observations);
  const monitorPeak = usesMonitor(methods) ? findFirstMonitorPeakDay(sorted) : null;

  if (usesSymptoThermal(methods)) {
    const pairs = findThermalShifts(sorted)
      .map((shift) => {
        const inRange = sorted.filter(
          (o) =>
            o.cycle_day >= shift.shiftDay - ST_PEAK_MAX_DAYS_BEFORE_SHIFT &&
            o.cycle_day <= shift.thirdHighDay &&
            isPeakTypeDay(o)
        );
        const onOrBeforeShift = inRange.filter((o) => o.cycle_day <= shift.shiftDay);
        return (onOrBeforeShift.length ? onOrBeforeShift : inRange).at(-1)?.cycle_day ?? null;
      })
      .filter((anchor): anchor is number => anchor !== null)
      .map((anchor) => getPeakClose(sorted, methods, anchor))
      .filter((close) => close.lastFertileCd !== null);

    if (monitorPeak !== null) {
      const monitorClose = getPeakClose(sorted, methods, monitorPeak);
      if (monitorClose.lastFertileCd !== null) return monitorPeak;
      if (!pairs.length && monitorClose.pending === "thermal-shift") return monitorPeak;
    }
    return pairs.length ? Math.max(...pairs.map((close) => close.effectivePeakCd)) : null;
  }

  if (usesExpandedMucus(methods)) {
    const firstPeakType = sorted.find(isPeakTypeDay);
    let mucusPeak: number | null = null;
    if (firstPeakType) {
      const close = getPeakClose(sorted, methods, firstPeakType.cycle_day);
      // Peak-type mucus still being seen: the Peak hasn't happened yet.
      if (close.lastFertileCd === null) return null;
      mucusPeak = close.effectivePeakCd;
    }
    const candidates = [mucusPeak, monitorPeak].filter((cd): cd is number => cd !== null);
    return candidates.length ? Math.max(...candidates) : null;
  }

  return null;
}

/**
 * First day after `afterCd` that looks like a new period: any Medium/Heavy bleeding, or
 * bleeding recorded on two consecutive days. Used when a cycle start wasn't marked, so a
 * cycle's post-Peak (infertile) phase isn't stretched across the next period and ovulation.
 * Never creates a cycle start — callers treat the days from here on as unknown and prompt.
 */
export function findLikelyNewCycleStart(
  observations: readonly { cycle_day: number; bleeding?: number | null }[],
  afterCd: number
): number | null {
  const bleedingByDay = new Map<number, number>();
  for (const obs of observations) {
    if (obs.bleeding && obs.bleeding > 0) bleedingByDay.set(obs.cycle_day, obs.bleeding);
  }
  const days = [...bleedingByDay.keys()].filter((cd) => cd > afterCd).sort((a, b) => a - b);
  for (const cd of days) {
    const code = bleedingByDay.get(cd);
    if (code === 2 || code === 3 || bleedingByDay.has(cd + 1)) return cd;
  }
  return null;
}

/**
 * Determine post-peak day sequence status ("complete" = the post-Peak infertile phase).
 *
 * - Sympto-Thermal and the mucus methods (Creighton/Billings), alone or with a monitor: time-
 *   based, from getPeakClose — "complete" only once the last observed day is past the method's
 *   last fertile day (Peak + 3, restarted by returning peak-type mucus; for Sympto-Thermal the
 *   later of that and the third higher temperature, with both signs required). The stages
 *   before that are labels for how far past the Peak the cycle is.
 * - Monitor-only (Marquette/Other): the monitor sequence 2 PEAK → 1 HIGH → 2 LOW → complete,
 *   counted from the readings after the Peak; a day with no reading is skipped, never assumed
 *   "low". Unchanged from v0.4.3.
 */
export function determinePostPeakProgress(
  observations: Observation[],
  firstPeakCD: number,
  methods: NFPMethod[] = ["Marquette"]
): PostPeakDay {
  // Get observations after peak
  const postPeakObs = observations
    .filter((o) => o.cycle_day > firstPeakCD)
    .sort((a, b) => a.cycle_day - b.cycle_day);

  if (postPeakObs.length === 0) return "2 PEAK";

  if (usesSymptoThermal(methods) || usesExpandedMucus(methods)) {
    const close = getPeakClose(observations, methods, firstPeakCD);
    const lastObservedDay = postPeakObs[postPeakObs.length - 1].cycle_day;
    if (close.lastFertileCd !== null && lastObservedDay > close.lastFertileCd) {
      return "complete";
    }
    const daysPastPeak = lastObservedDay - close.effectivePeakCd;
    if (daysPastPeak >= 2) return "2 LOW";
    if (daysPastPeak >= 1) return "1 HIGH";
    return "2 PEAK";
  }

  // Monitor-only: count the readings after the Peak. A day with no reading is skipped
  // entirely rather than silently counted as "low."
  let peakCount = 0;
  let highCount = 0;
  let lowCount = 0;

  for (const obs of postPeakObs) {
    const monitor = normalizeMonitor(obs.monitor);

    if (monitor) {
      if (monitor === "peak") peakCount++;
      else if (monitor === "high") highCount++;
      else lowCount++;
    }
    // else: no usable signal recorded that day — skip rather than assume "low."
  }

  // Check sequence completion
  if (peakCount >= 2 && highCount >= 1 && lowCount >= 2) {
    return "complete";
  } else if (peakCount >= 2 && highCount >= 1) {
    return "2 LOW";
  } else if (peakCount >= 2) {
    return "1 HIGH";
  } else {
    return "2 PEAK";
  }
}

/**
 * Analyze current cycle and provide fertility advice
 */
export function analyzeCycle(
  cycle: Cycle,
  observations: Observation[],
  goal: Goal,
  methods: NFPMethod[] = ["Marquette"],
  isPostpartum: boolean = false,
  timeZone?: string | null
): CycleAnalysis {
  if (goal === "Pregnant") {
    const today = getDateKeyInTimeZone(new Date(), timeZone);
    const currentDay =
      differenceInCalendarDays(parseISO(today), parseISO(cycle.start_date)) + 1;

    return {
      currentDay,
      fertilityAdvice: "ok",
      reason: "Pregnancy mode: informational tracking only",
      isPostPeak: false,
    };
  }

  const today = getDateKeyInTimeZone(new Date(), timeZone);
  const currentDay =
    differenceInCalendarDays(parseISO(today), parseISO(cycle.start_date)) + 1;

  // Sort observations by cycle day
  const sortedObs = [...observations].sort((a, b) => a.cycle_day - b.cycle_day);
  const todayObs = sortedObs.find((o) => o.date === today);

  // Check if bleeding
  if (todayObs?.bleeding && todayObs.bleeding > 0) {
    const advice = goal === "TTC" ? "ok" : goal === "Single" ? "ok" : "avoid";
    return {
      currentDay,
      fertilityAdvice: advice,
      reason: "Menstrual bleeding",
      isPostPeak: false,
    };
  }

  // A positive OPK is corroboration only: it is surfaced, never used to change advice.
  const lhSurgeCd = findLhSurgeDay(sortedObs);

  // Pre-peak phase (before first peak)
  if (!cycle.first_peak_cd) {
    // Worth calling out in the reason while no Peak has been identified yet.
    const withLhSurge = (analysis: CycleAnalysis): CycleAnalysis =>
      lhSurgeCd === null
        ? analysis
        : { ...analysis, lhSurgeCd, reason: `${analysis.reason} · LH surge CD ${lhSurgeCd}` };

    // Postpartum: no safe early days, always avoid until peak sequence complete
    if (isPostpartum) {
      const advice =
        goal === "TTC" ? "fertile" : goal === "Single" ? "fertile" : "avoid";
      return withLhSurge({
        currentDay,
        fertilityAdvice: advice,
        reason:
          goal === "Single"
            ? "Postpartum: Fertile window until peak sequence complete"
            : "Postpartum: Avoid until peak sequence complete",
        isPostPeak: false,
      });
    }

    const predictedAvoidStart = cycle.predicted_avoid_start_cd || 6;

    // Days 1-5 are typically safe for AP (non-postpartum)
    if (currentDay <= 5) {
      return withLhSurge({
        currentDay,
        fertilityAdvice: "ok",
        reason:
          goal === "Single"
            ? "Low fertility (Days 1-5)"
            : "Pre-ovulation (Days 1-5)",
        isPostPeak: false,
      });
    }

    // After predicted avoid start
    if (currentDay >= predictedAvoidStart) {
      const advice = goal === "AP" ? "avoid" : "fertile";
      return withLhSurge({
        currentDay,
        fertilityAdvice: advice,
        reason:
          goal === "Single"
            ? `Fertile window (CD ${predictedAvoidStart}+)`
            : goal === "TTC"
            ? `Fertile window (CD ${predictedAvoidStart}+)`
            : `Avoid - Fertile window (CD ${predictedAvoidStart}+)`,
        isPostPeak: false,
      });
    }

    return withLhSurge({
      currentDay,
      fertilityAdvice: "ok",
      reason:
        goal === "Single"
          ? `Low fertility (CD ${currentDay})`
          : `Pre-fertile window (CD ${currentDay})`,
      isPostPeak: false,
    });
  }

  // Post-peak phase (after first peak detected)
  const postPeakProgress = determinePostPeakProgress(
    sortedObs,
    cycle.first_peak_cd,
    methods
  );

  // Unmarked cycle start: a period after this cycle's window closed means a new cycle has most
  // likely begun, with its own fertile window ahead — never keep calling those days post-Peak.
  const lastFertileCd =
    getLastFertileCd(sortedObs, methods, cycle.first_peak_cd) ??
    cycle.first_peak_cd + POST_PEAK_COUNT_DAYS;
  const likelyNewCycleCd = findLikelyNewCycleStart(sortedObs, lastFertileCd);
  if (likelyNewCycleCd !== null && currentDay >= likelyNewCycleCd) {
    return {
      currentDay,
      fertilityAdvice: goal === "AP" ? "avoid" : "fertile",
      reason: `Bleeding on CD ${likelyNewCycleCd} looks like a new cycle — mark Cycle start on that day`,
      isPostPeak: false,
      lhSurgeCd,
    };
  }

  if (postPeakProgress === "complete") {
    return {
      currentDay,
      fertilityAdvice: "ok",
      reason:
        goal === "Single"
          ? "Post-peak infertile phase"
          : isPostpartum
          ? "Postpartum: Post-peak complete - OK"
          : "Post-peak infertile phase",
      postPeakProgress,
      isPostPeak: true,
      lhSurgeCd,
    };
  }

  const advice = goal === "AP" ? "avoid" : "fertile";
  return {
    currentDay,
    fertilityAdvice: advice,
    reason:
      goal === "Single"
        ? `Fertile window (${postPeakProgress})`
        : isPostpartum
        ? `Postpartum: Peak window (${postPeakProgress})`
        : `Peak/fertile window (${postPeakProgress})`,
    postPeakProgress,
    isPostPeak: true,
    lhSurgeCd,
  };
}

/**
 * Update cycle with new observation
 * Detects peaks and updates cycle state
 */
export function updateCycleWithObservation(
  cycle: Cycle,
  observation: Observation,
  allObservations: Observation[],
  methods: NFPMethod[] = ["Marquette"]
): Partial<Cycle> {
  const updates: Partial<Cycle> = {};

  // Detect first peak based on selected method(s), richest-signal-wins (see
  // determinePostPeakProgress above for the same precedence).
  if (!cycle.first_peak_cd) {
    if (usesSymptoThermal(methods) || usesExpandedMucus(methods)) {
      // Identified retrospectively over the whole cycle so far (see identifyPeakDay), so the
      // Peak may land on an EARLIER day than the observation that just arrived — e.g. the
      // non-peak day that confirms the mucus Peak, or the temperature that confirms a shift.
      const peak = identifyPeakDay(allObservations, methods);
      if (peak) {
        updates.first_peak_cd = peak;
      }
    } else {
      // Monitor-based methods, or no mucus data recorded yet: use monitor readings
      if (normalizeMonitor(observation.monitor) === "peak") {
        updates.first_peak_cd = observation.cycle_day;
      }
    }
  }

  // Update post-peak progress
  if (cycle.first_peak_cd) {
    const progress = determinePostPeakProgress(
      allObservations,
      cycle.first_peak_cd,
      methods
    );
    updates.post_peak_progress = progress;
  }

  return updates;
}

/**
 * Retroactively determine a single cycle's Peak Day from its full observation history,
 * by replaying updateCycleWithObservation day-by-day exactly as it would run if called live
 * as each day's observation came in. Reuses the existing peak-detection rule rather than
 * re-implementing it — cycles have no server-side representation (see derivedCycles.ts),
 * so this is what backfills first_peak_cd for cycles derived from historical data.
 */
export function computeFirstPeakDay(
  cycleObservations: Observation[],
  methods: NFPMethod[] = ["Marquette"]
): number | null {
  const sorted = [...cycleObservations].sort((a, b) => a.cycle_day - b.cycle_day);

  // Sympto-Thermal and mucus-method Peaks are only knowable in hindsight (a later non-peak
  // day or temperature shift confirms them), so they're computed over the whole cycle instead
  // of replayed — the same result whatever order the observations were logged in.
  if (usesSymptoThermal(methods) || usesExpandedMucus(methods)) {
    return identifyPeakDay(sorted, methods);
  }

  const cycleState: Cycle = { id: "replay", start_date: "", first_peak_cd: null };

  for (let i = 0; i < sorted.length; i++) {
    // Monitor-only methods: the first PEAK reading, exactly as the live path sees it.
    const observationsSoFar = sorted.slice(0, i + 1);
    const updates = updateCycleWithObservation(cycleState, sorted[i], observationsSoFar, methods);
    if (updates.first_peak_cd) {
      return updates.first_peak_cd;
    }
  }

  return null;
}

/**
 * Helper: Calculate days between two dates
 */
function daysBetween(start: string, end: string): number {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const diff = endDate.getTime() - startDate.getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

/**
 * Validate if cycle start timing is reasonable
 * Returns warning message if suspicious, null if OK
 */
export function validateCycleStart(currentCycleDay: number): string | null {
  if (currentCycleDay < 20) {
    return "Starting a new cycle before day 20 is unusual. Are you sure your period has started?";
  }
  return null;
}

/**
 * Validate observation data
 * Returns validation message if there are issues
 */
export function validateObservation(date: string): string | null {
  const obsDate = new Date(date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  obsDate.setHours(0, 0, 0, 0);

  const diffDays = Math.floor(
    (obsDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );

  if (diffDays > 0) {
    return "Cannot log observations for future dates.";
  }

  if (diffDays < -7) {
    return "Logging data more than 7 days in the past may affect cycle accuracy.";
  }

  return null;
}

/**
 * Get fertility advice display text
 */
export function getFertilityAdviceDisplay(
  advice: FertilityAdvice,
  goal: Goal
): {
  text: string;
  color: string;
  icon: string;
} {
  if (goal === "Pregnant") {
    return { text: "Pregnancy Tracking", color: "text-violet-600", icon: "🤰" };
  }

  switch (advice) {
    case "fertile":
      return {
        text: "Higher Fertility Estimate",
        color: goal === "TTC" ? "text-green-600" : "text-amber-600",
        icon: "🌟",
      };
    case "ok":
      return { text: "Lower Fertility Estimate", color: "text-blue-600", icon: "📅" };
    case "avoid":
      return { text: "Menstrual Tracking", color: "text-red-600", icon: "🩸" };
  }
}
